import React from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { NumberInput } from '@/components/ui/number-input'
import { Check, Download, X } from 'lucide-react'
import { cn, downloadFile, formatCurrency } from '@/lib/utils'
import {
  evaluateNewCompReadiness,
  normalizeNewCompParticipants,
  normalizeNewCompPlanFacts,
} from '@/lib/newCompInputs'
import { solveNewCompScenarios } from '@/lib/newCompScenarios'
import { solveCashBalanceComboScenarios } from '@/lib/comboScenarios'
import {
  buildNewCompCalculationPayload,
  buildNewCompProposal,
  canApplyScenario,
  newCompPayloadToCsv,
} from '@/lib/newCompProposal'
import { buildNewCompProposalModel } from '@/lib/newCompProposalModel'
import { MODELING_INPUT_LIMITS, NEW_COMP_MODELING_DEFAULTS } from '@/lib/modelingDefaults'

// Guided new-comparability workflow (one-shot plan §UI), replacing the single
// solve panel: Readiness → Goals → Scenarios → Tests and worksheet → Apply.
// Ordinary sponsors see three goal presets and scenario cards; every TPA lever
// stays inside the Advanced disclosure. Statuses use the shared honest
// vocabulary (passes / fails / requires-review / unavailable / not-applicable)
// and applying a requires-review design records an explicit acknowledgment.
// Nothing changes until Apply, and census rows are never written.

const TARGET_GROUP = 'Owner'
const HCE_GROUP = 'Non-Owner HCE'
const STAFF_GROUP = 'Staff'

const STATUS_BADGES = {
  passes: { symbol: '✓', className: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  fails: { symbol: '✗', className: 'bg-red-500/15 text-red-700 dark:text-red-300' },
  'requires-review': { symbol: '⚠', className: 'bg-amber-500/15 text-amber-800 dark:text-amber-300' },
  unavailable: { symbol: '•', className: 'bg-muted text-muted-foreground' },
  'not-applicable': { symbol: '·', className: 'bg-muted text-muted-foreground' },
}

const STATUS_LABELS = {
  passes: 'No modeled flag',
  fails: 'Flagged for review',
  'requires-review': 'Professional review needed',
  unavailable: 'Unavailable',
  'not-applicable': 'Not applicable',
}

// Badge labels for both workflows: the DC-only screens and the combined
// DB/DC screens. Rendered from whatever keys the scenario's tests carry.
const TEST_BADGE_LABELS = {
  gateway: 'Gateway',
  coverage: 'Coverage',
  rateGroups: 'Rate groups',
  abpt: 'ABPT',
  section415c: '415(c)',
  deduction: '404 screen',
  topHeavy: 'Top-heavy',
  minimumParticipation: '401(a)(26)',
  combinedGateway: 'Aggregate gateway',
  mostValuable: 'Most valuable',
  section415b: '415(b)',
  deduction404a7: '404(a)(7)',
}

function StatusBadge({ status, children }) {
  const badge = STATUS_BADGES[status] || STATUS_BADGES.unavailable
  return (
    <span className={cn('inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium', badge.className)}>
      {badge.symbol} {children}
    </span>
  )
}

function StepHeading({ number, title, hint }) {
  return (
    <div className="flex items-baseline gap-2 border-t border-border/60 pt-3 first:border-t-0 first:pt-0">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold">{number}</span>
      <div>
        <p className="text-sm font-semibold">{title}</p>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
    </div>
  )
}

const dollars = (cents) => formatCurrency((Number(cents) || 0) / 100)
const proposalDollars = (cents) => cents === null || cents === undefined ? 'Actuary input needed' : dollars(cents)

export default function NewCompSolvePanel({
  employees,
  calculatedEmployees,
  plan = null,
  employerProfile = null,
  metrics = null,
  onApply,
  onClose,
  showHeader = true,
}) {
  const nameById = React.useMemo(
    () => Object.fromEntries((employees || []).map((employee) => [employee.id, employee.name || employee.id])),
    [employees])

  // Candidate owners/HCEs, most-likely target first (existing convention).
  const hceRows = React.useMemo(() => (calculatedEmployees || [])
    .filter((row) => row.isHCE && row.status !== 'Terminated')
    .sort((a, b) => (b.isFivePercentOwner ? 1 : 0) - (a.isFivePercentOwner ? 1 : 0)
      || (Number(b.ownershipPercent) || 0) - (Number(a.ownershipPercent) || 0)
      || (Number(b.cappedCompensation) || 0) - (Number(a.cappedCompensation) || 0)),
    [calculatedEmployees])

  const [preset, setPreset] = React.useState('maximize')
  const [targetIds, setTargetIds] = React.useState(() => {
    const owners = hceRows.filter((row) => row.isFivePercentOwner).map((row) => row.id)
    return owners.length ? owners : hceRows.slice(0, 1).map((row) => row.id)
  })
  const [targetMode, setTargetMode] = React.useState('dollars')
  const [targetsById, setTargetsById] = React.useState({})
  const [budget, setBudget] = React.useState(NEW_COMP_MODELING_DEFAULTS.budgetDollars)
  const [hcePercent, setHcePercent] = React.useState(NEW_COMP_MODELING_DEFAULTS.nonTargetHceProfitSharingPercent)
  const [maxStaffPercent, setMaxStaffPercent] = React.useState(NEW_COMP_MODELING_DEFAULTS.maxStaffProfitSharingPercent)
  const [assumeTopHeavy, setAssumeTopHeavy] = React.useState('')
  const [attest414s, setAttest414s] = React.useState(false)
  const [routeQualificationConfirmed, setRouteQualificationConfirmed] = React.useState(
    plan?.nondiscriminationTesting?.gatewayRouteQualification?.confirmed === true)
  const [routeQualificationSource, setRouteQualificationSource] = React.useState(
    plan?.nondiscriminationTesting?.gatewayRouteQualification?.source || 'tpa')
  // Combined DB/DC facts (cash-balance component enabled): aggregation is an
  // explicit confirmation, averaging is an election, PBGC status is a fact.
  const [aggregateConfirmed, setAggregateConfirmed] = React.useState(false)
  const [averagingElected, setAveragingElected] = React.useState(false)
  const [pbgcCovered, setPbgcCovered] = React.useState('unknown')
  const [fundingYears, setFundingYears] = React.useState(NEW_COMP_MODELING_DEFAULTS.ownerFundingYears)
  const [comboTargetPsPercent, setComboTargetPsPercent] = React.useState(NEW_COMP_MODELING_DEFAULTS.ownerProfitSharingPercent)
  const [comboHceCbPercent, setComboHceCbPercent] = React.useState(NEW_COMP_MODELING_DEFAULTS.nonTargetHceCashBalancePercent)
  const [comboStaffCbPercent, setComboStaffCbPercent] = React.useState(NEW_COMP_MODELING_DEFAULTS.staffCashBalancePercent)
  const [comboMaxStaffCbPercent, setComboMaxStaffCbPercent] = React.useState(NEW_COMP_MODELING_DEFAULTS.maxStaffCashBalancePercent)
  const [comboMaxStaffPsPercent, setComboMaxStaffPsPercent] = React.useState(NEW_COMP_MODELING_DEFAULTS.maxComboStaffProfitSharingPercent)
  const [projectionYears, setProjectionYears] = React.useState(NEW_COMP_MODELING_DEFAULTS.projectionYears)
  const [marginalTaxRatePercent, setMarginalTaxRatePercent] = React.useState(NEW_COMP_MODELING_DEFAULTS.marginalTaxRatePercent)
  const [salaryGrowthPercent, setSalaryGrowthPercent] = React.useState(NEW_COMP_MODELING_DEFAULTS.salaryGrowthPercent)
  const [fundingCorridor, setFundingCorridor] = React.useState({ minimum: '', recommended: '', maximum: '', source: '', asOf: '', note: '' })
  const [beneficiaryReviewed, setBeneficiaryReviewed] = React.useState(false)
  const [spousalConsentApplies, setSpousalConsentApplies] = React.useState(true)
  const [qjsaApplies, setQjsaApplies] = React.useState(true)

  const cbEnabled = plan?.cashBalance?.enabled === true
  const [selectedScenarioId, setSelectedScenarioId] = React.useState(null)
  const [acknowledged, setAcknowledged] = React.useState(false)
  const [ackNote, setAckNote] = React.useState('')

  const toggleTarget = (id) => setTargetIds((prev) => (
    prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]
  ))

  // ---- Normalized inputs, readiness, and the solve (all pure) ----
  const planFacts = React.useMemo(() => {
    const metricsStatus = metrics?.topHeavyStatus
    const topHeavy = metricsStatus === 'top-heavy' || metricsStatus === 'not-top-heavy'
      ? { status: metricsStatus, minimumPercent: metrics?.topHeavyMinPercent ?? null, source: 'calculated' }
      : assumeTopHeavy
        ? { status: assumeTopHeavy, minimumPercent: 3, source: 'assumed' }
        : { status: 'unknown', minimumPercent: null, source: null }
    const normalized = normalizeNewCompPlanFacts(plan || {}, {
      employerProfile,
      topHeavy,
      compensation414sAttested: attest414s,
      dbDcAggregation: cbEnabled
        ? {
            aggregate: aggregateConfirmed,
            nhceDbAveragingElection: averagingElected,
            primarilyDbCharacter: 'unknown',
            broadlyAvailableSeparatePlans: 'unknown',
            gatewayMethod: 'sliding',
          }
        : null,
    })
    return normalized.gatewayRoute === 'minimum-allocation'
      ? normalized
      : {
          ...normalized,
          gatewayRouteQualification: routeQualificationConfirmed
            ? { confirmed: true, source: routeQualificationSource, note: 'Confirmed in the proposal workflow.' }
            : null,
        }
  }, [plan, employerProfile, metrics, assumeTopHeavy, attest414s, cbEnabled, aggregateConfirmed, averagingElected,
    routeQualificationConfirmed, routeQualificationSource])

  const participants = React.useMemo(
    () => normalizeNewCompParticipants(calculatedEmployees || [], employees || []),
    [calculatedEmployees, employees])

  const readiness = React.useMemo(
    () => evaluateNewCompReadiness({ planFacts, participants }),
    [planFacts, participants])

  const goal = React.useMemo(() => {
    if (preset === 'budget') return { objective: 'budget-cap', targetIds, budget }
    if (preset === 'targets') {
      return {
        objective: targetMode === 'percent' ? 'fixed-target-percentages' : 'fixed-target-dollars',
        targetIds,
        targetsById,
      }
    }
    return { objective: 'maximize-targets', targetIds }
  }, [preset, targetIds, targetsById, targetMode, budget])

  const reportInputs = React.useMemo(() => ({
    projectionYears,
    marginalTaxRatePercent,
    salaryGrowthPercent,
    fundingCorridor,
    disclosures: {
      beneficiaryDesignationReviewed: beneficiaryReviewed,
      spousalConsentApplies,
      qjsaApplies,
    },
  }), [projectionYears, marginalTaxRatePercent, salaryGrowthPercent, fundingCorridor,
    beneficiaryReviewed, spousalConsentApplies, qjsaApplies])

  const solved = React.useMemo(() => {
    if (readiness.status === 'blocked' || !targetIds.length) return null
    if (cbEnabled) {
      // Combined DB/DC designer: the cash-balance component makes the
      // combined path mandatory; the DC-only engine is not consulted.
      return solveCashBalanceComboScenarios({
        participants,
        planFacts,
        cbFacts: {
          interestCreditRate: planFacts.cashBalance?.interestCreditRate
            ?? NEW_COMP_MODELING_DEFAULTS.cashBalanceInterestCreditRate,
          fundingYears,
          dbPbgcCovered: pbgcCovered,
          aggregationFacts: planFacts.dbDcAggregation,
        },
        goal: { targetIds, budget: preset === 'budget' ? budget : null },
        options: {
          targetPsPercent: comboTargetPsPercent,
          nonTargetHceCbPercent: comboHceCbPercent,
          nonTargetHcePsPercent: hcePercent,
          staffCbPercent: comboStaffCbPercent,
          maxStaffCbPercent: comboMaxStaffCbPercent,
          maxStaffPsPercent: comboMaxStaffPsPercent,
        },
        readiness,
        reportInputs,
      })
    }
    return solveNewCompScenarios({
      participants,
      planFacts,
      goal,
      options: { nonTargetHcePercent: hcePercent, maxStaffPercent },
      readiness,
      reportInputs,
    })
  }, [participants, planFacts, goal, hcePercent, maxStaffPercent, readiness, targetIds,
    cbEnabled, fundingYears, pbgcCovered, preset, budget, comboTargetPsPercent, comboHceCbPercent,
    comboStaffCbPercent, comboMaxStaffCbPercent, comboMaxStaffPsPercent, reportInputs])

  const scenarios = solved?.scenarios || []
  const selectedScenario = scenarios.find((scenario) => scenario.id === selectedScenarioId)
    || (scenarios.length === 1 ? scenarios[0] : null)
  const applyReady = selectedScenario
    && canApplyScenario(selectedScenario, { acknowledged })
  const needsAcknowledgment = selectedScenario?.status?.status === 'requires-review'
  const proposalModel = React.useMemo(() => selectedScenario
    ? buildNewCompProposalModel({
        solveResult: solved,
        scenario: selectedScenario,
        participants,
        reportInputs,
        planYear: planFacts.planYear,
      })
    : null, [selectedScenario, solved, participants, reportInputs, planFacts.planYear])
  const proposalMetricsById = React.useMemo(() => Object.fromEntries(
    (proposalModel?.scenarioPortfolio || []).map((entry) => [entry.id, entry])), [proposalModel])
  const gatewayNhceSourceTotals = React.useMemo(() => {
    const rows = (selectedScenario?.perPerson || []).filter((row) => row.isHCE !== true)
    return rows.reduce((totals, row) => ({
      safeHarborNecCents: totals.safeHarborNecCents + (Number(row.safeHarborNonelectiveCents) || 0),
      otherNonelectiveCents: totals.otherNonelectiveCents
        + (Number(row.otherNonelectiveCents) || 0)
        + (Number(row.qnecCents) || 0)
        + (Number(row.forfeitureCents) || 0),
      profitSharingCents: totals.profitSharingCents + (Number(row.profitSharingCents) || 0),
      matchCents: totals.matchCents + (Number(row.matchCents) || 0),
      deferralCents: totals.deferralCents + (Number(row.deferralCents) || 0),
    }), {
      safeHarborNecCents: 0,
      otherNonelectiveCents: 0,
      profitSharingCents: 0,
      matchCents: 0,
      deferralCents: 0,
    })
  }, [selectedScenario])

  const handleApply = () => {
    if (!selectedScenario || !applyReady) return
    const proposal = buildNewCompProposal({
      solveResult: solved,
      scenario: selectedScenario,
      acknowledgment: needsAcknowledgment
        ? { acknowledged: true, note: ackNote, at: new Date().toISOString() }
        : null,
    })
    const outcome = onApply({
      design: selectedScenario.suggestedDesign,
      groupAssignments: selectedScenario.groupAssignments,
      staffPercent: selectedScenario.staffPercent,
      proposal,
      ...(selectedScenario.suggestedCashBalance
        ? { cashBalance: selectedScenario.suggestedCashBalance }
        : {}),
    }) || {}
    if (outcome.applied === false) {
      toast.error('Design was not applied', {
        description: outcome.demoBlocked
          ? 'Applying plan-specific groups to the full employee list is a Studio feature. Open Studio and apply the design there.'
          : 'The plan and its participant assignments could not be saved together. Review the plan and try again.',
      })
      return
    }
    toast.success(`Applied new-comp design: staff class at ${selectedScenario.staffPercent.toFixed(2)}%`)
    onClose?.()
  }

  const handleDownload = async (format) => {
    if (!selectedScenario) return
    const payload = buildNewCompCalculationPayload({
      solveResult: solved,
      scenario: selectedScenario,
      planFacts,
      participants,
      planName: plan?.name ?? null,
      employerName: employerProfile?.legalName ?? employerProfile?.name ?? null,
      reportInputs,
    })
    if (format === 'json') {
      downloadFile(JSON.stringify(payload, null, 2), 'new-comparability-calculation-package.json', 'application/json')
    } else downloadFile(newCompPayloadToCsv(payload), 'new-comparability-worksheets.csv', 'text/csv')
  }

  const employeeNames = (ids = []) => ids.map((id) => nameById[id] || id).join(', ')

  return (
    <div
      className={showHeader ? 'min-w-0 space-y-4 rounded-md border border-primary/30 bg-primary/[0.03] p-3' : 'min-w-0 space-y-4'}
      data-testid="new-comp-solve-panel"
    >
      {showHeader && (
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-sm font-semibold">Design new comparability</p>
            <p className="text-xs text-muted-foreground">
              A guided, modeled proposal on documented assumptions. Not a formal nondiscrimination
              determination or advice.
            </p>
          </div>
          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 shrink-0 p-0" aria-label="Close solve panel" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* ---- Step 1: Readiness ---- */}
      <section className="space-y-2" data-testid="newcomp-step-readiness">
        <StepHeading
          number={1}
          title="Readiness"
          hint={`${readiness.includedCount} employees, ${readiness.hceCount} HCE / ${readiness.nhceCount} staff. Blockers stop the design; review items go to the TPA.`}
        />
        {readiness.blockers.length === 0 ? (
          <p className="text-xs text-emerald-700 dark:text-emerald-300">No blockers. The employee list and plan terms are sufficient to model this design.</p>
        ) : (
          <ul className="space-y-1.5">
            {readiness.blockers.map((blocker) => (
              <li key={blocker.code + (blocker.employeeIds || []).join(',')} className="rounded-md border border-red-300/60 bg-red-500/5 px-2.5 py-1.5 text-xs">
                <p className="font-medium text-red-700 dark:text-red-300">{blocker.message}</p>
                {blocker.employeeIds?.length > 0 && (
                  <p className="text-muted-foreground">Affected: {employeeNames(blocker.employeeIds)}</p>
                )}
                {blocker.nextAction && <p className="text-muted-foreground">Next: {blocker.nextAction}</p>}
              </li>
            ))}
          </ul>
        )}
        {readiness.reviewItems.length > 0 && (
          <details className="rounded-md border border-dashed border-amber-400/50 p-2 text-xs">
            <summary className="cursor-pointer font-medium text-amber-800 dark:text-amber-300">
              {readiness.reviewItems.length} item{readiness.reviewItems.length === 1 ? '' : 's'} for professional review
            </summary>
            <ul className="mt-1.5 list-disc space-y-1 pl-4 text-muted-foreground">
              {readiness.reviewItems.map((item) => (
                <li key={item.code + (item.employeeIds || []).join(',')}>
                  {item.message}
                  {item.employeeIds?.length > 0 && <> ({employeeNames(item.employeeIds)})</>}
                  {item.nextAction && <span className="block text-[11px]">Next: {item.nextAction}</span>}
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>

      {/* ---- Step 2: Goals ---- */}
      <section className="space-y-2.5" data-testid="newcomp-step-goals">
        <StepHeading number={2} title="Goal" hint="Pick what the design should optimize." />
        <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Design goal">
          {(cbEnabled
            ? [
                ['maximize', 'Maximize owners (cash balance + PS)'],
                ['budget', 'Stay within a budget'],
              ]
            : [
                ['maximize', 'Maximize selected owners'],
                ['targets', 'Set target contributions'],
                ['budget', 'Stay within a budget'],
              ]
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={preset === value}
              onClick={() => { setPreset(value); setSelectedScenarioId(null); setAcknowledged(false) }}
              className={cn(
                'rounded-md border px-2.5 py-1.5 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                preset === value ? 'border-primary bg-primary/10 text-foreground' : 'border-input text-muted-foreground',
              )}
            >
              {label}
            </button>
          ))}
        </div>

        <div>
          <Label className="text-xs font-medium text-muted-foreground">Design around these owners / HCEs</Label>
          <div className="mt-1 flex flex-wrap gap-2">
            {hceRows.length === 0 && (
              <p className="text-xs text-muted-foreground">No HCEs found. Mark an owner (Owner &gt;5%, Own %, or Family Of) first.</p>
            )}
            {hceRows.map((row) => (
              <button
                key={row.id}
                type="button"
                onClick={() => { toggleTarget(row.id); setSelectedScenarioId(null) }}
                className={cn(
                  'flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  targetIds.includes(row.id) ? 'border-primary bg-primary/10 text-foreground' : 'border-input text-muted-foreground',
                )}
                aria-pressed={targetIds.includes(row.id)}
              >
                <span className={cn(
                  'flex h-3.5 w-3.5 items-center justify-center rounded-sm border',
                  targetIds.includes(row.id) ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
                )}>
                  {targetIds.includes(row.id) && <Check className="h-2.5 w-2.5" aria-hidden="true" />}
                </span>
                {nameById[row.id]}
                {row.isFivePercentOwner && <span className="text-[10px] uppercase text-primary">owner</span>}
              </button>
            ))}
          </div>
        </div>

        {preset === 'targets' && (
          <div className="space-y-1.5 rounded-md border border-border/60 bg-background/60 p-2.5">
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">Targets as</Label>
              {['dollars', 'percent'].map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => { setTargetMode(mode); setSelectedScenarioId(null) }}
                  aria-pressed={targetMode === mode}
                  className={cn('rounded border px-2 py-0.5 text-xs', targetMode === mode ? 'border-primary bg-primary/10' : 'border-input text-muted-foreground')}
                >
                  {mode === 'dollars' ? 'Dollars' : '% of pay'}
                </button>
              ))}
            </div>
            {targetIds.map((id) => (
              <div key={id} className="flex items-center gap-2">
                <span className="min-w-[10rem] text-xs">{nameById[id]}</span>
                {targetMode === 'dollars' && <span className="text-xs">$</span>}
                <NumberInput
                  aria-label={`${nameById[id]} target ${targetMode === 'percent' ? 'percent' : 'dollars'}`}
                  value={targetsById[id] ?? ''}
                  onValueChange={(value) => {
                    setTargetsById((prev) => ({ ...prev, [id]: Math.max(0, Number(value) || 0) }))
                    setSelectedScenarioId(null)
                  }}
                  className="h-7 w-28 text-right text-xs"
                  min={0}
                  showError={false}
                  placeholder="0"
                />
                {targetMode === 'percent' && <span className="text-xs text-muted-foreground">%</span>}
              </div>
            ))}
          </div>
        )}

        {preset === 'budget' && (
          <div className="flex items-center gap-2">
            <Label htmlFor="newcomp-budget" className="text-xs text-muted-foreground">Total employer budget</Label>
            <span className="text-xs">$</span>
            <NumberInput
              id="newcomp-budget"
              value={budget}
              onValueChange={(value) => { setBudget(Math.max(0, Number(value) || 0)); setSelectedScenarioId(null) }}
              className="h-7 w-32 text-right text-xs"
              min={0}
              showError={false}
            />
          </div>
        )}

        {cbEnabled && !aggregateConfirmed && (
          <p className="rounded-md border border-amber-400/60 bg-amber-500/5 p-2.5 text-xs" data-testid="newcomp-combined-required">
            This plan has an enabled cash-balance component, so combined DB/DC testing is required before any
            modeled pass. Confirm aggregation under Advanced / TPA settings to generate combined scenarios.
          </p>
        )}
        <details className="rounded-md border border-dashed p-2 text-xs text-muted-foreground" data-testid="newcomp-details">
          <summary className="cursor-pointer font-medium text-foreground">Advanced / TPA settings</summary>
          <div className="mt-2 space-y-2">
            {cbEnabled && (
              <div className="space-y-2 rounded-md border border-border/60 p-2">
                <p className="font-medium text-foreground">Combined DB/DC testing facts</p>
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={aggregateConfirmed}
                    onChange={(event) => { setAggregateConfirmed(event.target.checked); setSelectedScenarioId(null); setAcknowledged(false) }}
                    className="mt-0.5"
                    data-testid="newcomp-aggregate-confirm"
                  />
                  <span>
                    Test the cash-balance and DC components together (permissive aggregation). Plans are never
                    aggregated automatically; this records your instruction to test on the combined basis.
                  </span>
                </label>
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={averagingElected}
                    onChange={(event) => { setAveragingElected(event.target.checked); setSelectedScenarioId(null) }}
                    className="mt-0.5"
                    data-testid="newcomp-averaging"
                  />
                  <span>Elect NHCE cash-balance equivalent-rate averaging for the aggregate gateway.</span>
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  <Label className="text-xs">DB plan PBGC-covered?</Label>
                  {['unknown', 'yes', 'no'].map((value) => (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={pbgcCovered === value}
                      onClick={() => { setPbgcCovered(value); setSelectedScenarioId(null) }}
                      className={cn('rounded border px-2 py-0.5 text-xs', pbgcCovered === value ? 'border-primary bg-primary/10 text-foreground' : 'border-input')}
                    >
                      {value}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Label htmlFor="newcomp-funding-years" className="text-xs">Owner funding period</Label>
                  <NumberInput
                    id="newcomp-funding-years"
                    value={fundingYears}
                    onValueChange={(value) => { setFundingYears(Math.min(MODELING_INPUT_LIMITS.maxOwnerFundingYears, Math.max(1, Math.round(Number(value) || NEW_COMP_MODELING_DEFAULTS.ownerFundingYears)))); setSelectedScenarioId(null) }}
                    className="h-7 w-16 text-right text-xs"
                    min={1}
                    max={MODELING_INPUT_LIMITS.maxOwnerFundingYears}
                    integer
                    showError={false}
                  />
                  <span>years</span>
                  <Label htmlFor="newcomp-combo-target-ps" className="ml-2 text-xs">Owner PS</Label>
                  <NumberInput
                    id="newcomp-combo-target-ps"
                    value={comboTargetPsPercent}
                    onValueChange={(value) => { setComboTargetPsPercent(Math.max(0, Number(value) || 0)); setSelectedScenarioId(null) }}
                    className="h-7 w-16 text-right text-xs"
                    min={0}
                    showError={false}
                  />
                  <span>%</span>
                  <Label htmlFor="newcomp-combo-hce-cb" className="ml-2 text-xs">Non-owner HCE CB</Label>
                  <NumberInput
                    id="newcomp-combo-hce-cb"
                    value={comboHceCbPercent}
                    onValueChange={(value) => { setComboHceCbPercent(Math.max(0, Number(value) || 0)); setSelectedScenarioId(null) }}
                    className="h-7 w-16 text-right text-xs"
                    min={0}
                    showError={false}
                  />
                  <span>%</span>
                  <Label htmlFor="newcomp-combo-staff-cb" className="ml-2 text-xs">Staff CB from</Label>
                  <NumberInput
                    id="newcomp-combo-staff-cb"
                    value={comboStaffCbPercent}
                    onValueChange={(value) => { setComboStaffCbPercent(Math.max(0, Number(value) || 0)); setSelectedScenarioId(null) }}
                    className="h-7 w-16 text-right text-xs"
                    min={0}
                    showError={false}
                  />
                  <span>%</span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Label htmlFor="newcomp-combo-max-staff-cb" className="text-xs">Staff CB cap</Label>
                  <NumberInput
                    id="newcomp-combo-max-staff-cb"
                    value={comboMaxStaffCbPercent}
                    onValueChange={(value) => { setComboMaxStaffCbPercent(Math.max(0, Number(value) || 0)); setSelectedScenarioId(null) }}
                    className="h-7 w-16 text-right text-xs"
                    min={0}
                    showError={false}
                  />
                  <span>%</span>
                  <Label htmlFor="newcomp-combo-max-staff-ps" className="ml-2 text-xs">Staff PS cap</Label>
                  <NumberInput
                    id="newcomp-combo-max-staff-ps"
                    value={comboMaxStaffPsPercent}
                    onValueChange={(value) => { setComboMaxStaffPsPercent(Math.max(0, Number(value) || 0)); setSelectedScenarioId(null) }}
                    className="h-7 w-16 text-right text-xs"
                    min={0}
                    showError={false}
                  />
                  <span>%</span>
                </div>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Label htmlFor="newcomp-hce-percent" className="text-xs">Non-target HCEs get a flat</Label>
              <NumberInput
                id="newcomp-hce-percent"
                value={hcePercent}
                onValueChange={(value) => { setHcePercent(Math.max(0, Number(value) || 0)); setSelectedScenarioId(null) }}
                className="h-7 w-20 text-right text-xs"
                min={0}
                showError={false}
              />
              <span>% of pay</span>
              <Label htmlFor="newcomp-max-staff" className="ml-3 text-xs">Staff rate cap</Label>
              <NumberInput
                id="newcomp-max-staff"
                value={maxStaffPercent}
                onValueChange={(value) => { setMaxStaffPercent(Math.max(1, Number(value) || NEW_COMP_MODELING_DEFAULTS.maxStaffProfitSharingPercent)); setSelectedScenarioId(null) }}
                className="h-7 w-20 text-right text-xs"
                min={1}
                showError={false}
              />
              <span>%</span>
            </div>
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={attest414s}
                onChange={(event) => { setAttest414s(event.target.checked); setSelectedScenarioId(null); setAcknowledged(false) }}
                className="mt-0.5"
                data-testid="newcomp-attest-414s"
              />
              <span>
                The compensation entered is a §414(s) safe-harbor definition (for example W-2 or §415 pay with no
                exclusions). This attestation is recorded with the result; Retira does not verify it.
              </span>
            </label>
            {planFacts.gatewayRoute !== 'minimum-allocation' && (
              <div className="space-y-1.5 rounded-md border border-amber-400/50 bg-amber-500/5 p-2">
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={routeQualificationConfirmed}
                    onChange={(event) => { setRouteQualificationConfirmed(event.target.checked); setSelectedScenarioId(null); setAcknowledged(false) }}
                    className="mt-0.5"
                  />
                  <span>
                    A qualified source confirmed the {planFacts.gatewayRoute} formula and availability conditions in the plan document.
                    The app still flags an apparent broadly-available rate-group shortfall for review.
                  </span>
                </label>
                {routeQualificationConfirmed && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span>Confirmation source:</span>
                    {[
                      ['tpa', 'TPA'],
                      ['actuary', 'Actuary'],
                      ['plan-document', 'Plan document'],
                    ].map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        aria-pressed={routeQualificationSource === value}
                        onClick={() => { setRouteQualificationSource(value); setSelectedScenarioId(null) }}
                        className={cn('rounded border px-2 py-0.5', routeQualificationSource === value ? 'border-primary bg-primary/10 text-foreground' : 'border-input')}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {planFacts.topHeavy.status === 'unknown' && (
              <div className="flex flex-wrap items-center gap-2">
                <Label className="text-xs">Top-heavy status is unknown. Model an assumption:</Label>
                {['', 'top-heavy', 'not-top-heavy'].map((value) => (
                  <button
                    key={value || 'none'}
                    type="button"
                    aria-pressed={assumeTopHeavy === value}
                    onClick={() => { setAssumeTopHeavy(value); setSelectedScenarioId(null) }}
                    className={cn('rounded border px-2 py-0.5 text-xs', assumeTopHeavy === value ? 'border-primary bg-primary/10 text-foreground' : 'border-input')}
                  >
                    {value === '' ? 'No assumption' : value === 'top-heavy' ? 'Assume top-heavy (3% min)' : 'Assume not top-heavy'}
                  </button>
                ))}
              </div>
            )}
            <div className="space-y-2 rounded-md border border-border/60 p-2">
              <p className="font-medium text-foreground">Client proposal assumptions</p>
              <div className="grid gap-2 sm:grid-cols-3">
                <Label className="space-y-1 text-xs">
                  <span>Projection years</span>
                  <NumberInput
                    aria-label="Proposal projection years"
                    value={projectionYears}
                    onValueChange={(value) => setProjectionYears(Math.min(MODELING_INPUT_LIMITS.maxProjectionYears, Math.max(1, Math.round(Number(value) || NEW_COMP_MODELING_DEFAULTS.projectionYears))))}
                    min={1}
                    max={MODELING_INPUT_LIMITS.maxProjectionYears}
                    integer
                    showError={false}
                  />
                </Label>
                <Label className="space-y-1 text-xs">
                  <span>Marginal tax rate</span>
                  <span className="flex items-center gap-1">
                    <NumberInput
                      aria-label="Proposal marginal tax rate"
                      value={marginalTaxRatePercent}
                      onValueChange={(value) => setMarginalTaxRatePercent(Math.min(MODELING_INPUT_LIMITS.maxMarginalTaxRatePercent, Math.max(0, Number(value) || 0)))}
                      min={0}
                      max={MODELING_INPUT_LIMITS.maxMarginalTaxRatePercent}
                      step="0.5"
                      showError={false}
                    />%
                  </span>
                </Label>
                <Label className="space-y-1 text-xs">
                  <span>Annual pay growth</span>
                  <span className="flex items-center gap-1">
                    <NumberInput
                      aria-label="Proposal annual pay growth"
                      value={salaryGrowthPercent}
                      onValueChange={(value) => setSalaryGrowthPercent(Math.min(MODELING_INPUT_LIMITS.maxSalaryGrowthPercent, Math.max(0, Number(value) || 0)))}
                      min={0}
                      max={MODELING_INPUT_LIMITS.maxSalaryGrowthPercent}
                      step="0.25"
                      showError={false}
                    />%
                  </span>
                </Label>
              </div>
              {cbEnabled && (
                <div className="space-y-2 border-t border-border/60 pt-2">
                  <p className="text-xs font-medium text-foreground">Actuary-supplied funding corridor</p>
                  <div className="grid gap-2 sm:grid-cols-3">
                    {[
                      ['minimum', 'Minimum funding'],
                      ['recommended', 'Recommended funding'],
                      ['maximum', 'Maximum funding'],
                    ].map(([key, label]) => (
                      <Label key={key} className="space-y-1 text-xs">
                        <span>{label}</span>
                        <span className="flex items-center gap-1">$
                          <NumberInput
                            aria-label={label}
                            value={fundingCorridor[key]}
                            placeholder="Not supplied"
                            onValueChange={(value) => setFundingCorridor((current) => ({ ...current, [key]: value }))}
                            min={0}
                            step="1000"
                            showError={false}
                          />
                        </span>
                      </Label>
                    ))}
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Label className="space-y-1 text-xs">
                      <span>Actuary or source</span>
                      <input
                        type="text"
                        value={fundingCorridor.source}
                        onChange={(event) => setFundingCorridor((current) => ({ ...current, source: event.target.value }))}
                        placeholder="Firm or actuary name"
                        className="h-8 w-full rounded-md border border-input bg-background px-2"
                      />
                    </Label>
                    <Label className="space-y-1 text-xs">
                      <span>As-of date</span>
                      <input
                        type="date"
                        value={fundingCorridor.asOf}
                        onChange={(event) => setFundingCorridor((current) => ({ ...current, asOf: event.target.value }))}
                        className="h-8 w-full rounded-md border border-input bg-background px-2"
                      />
                    </Label>
                  </div>
                  <p>No funding amount is calculated by the app. Blank fields stay labeled as requiring actuary input.</p>
                </div>
              )}
              <div className="space-y-1 border-t border-border/60 pt-2">
                <p className="text-xs font-medium text-foreground">Participant communication checklist</p>
                <label className="flex items-start gap-2"><input type="checkbox" checked={beneficiaryReviewed} onChange={(event) => setBeneficiaryReviewed(event.target.checked)} className="mt-0.5" /><span>Beneficiary designation review is included in the implementation follow-up.</span></label>
                <label className="flex items-start gap-2"><input type="checkbox" checked={spousalConsentApplies} onChange={(event) => setSpousalConsentApplies(event.target.checked)} className="mt-0.5" /><span>Spousal consent rules apply unless the TPA confirms an exception.</span></label>
                <label className="flex items-start gap-2"><input type="checkbox" checked={qjsaApplies} onChange={(event) => setQjsaApplies(event.target.checked)} className="mt-0.5" /><span>QJSA and optional-form notices apply unless the plan document says otherwise.</span></label>
              </div>
            </div>
            <p>
              Document elections (edit in Plan settings): gateway route {planFacts.gatewayRoute}, gateway method {planFacts.gatewayMinimumMethod},
              otherwise-excludable {planFacts.otherwiseExcludableElection ? 'on' : 'off'}, imputed disparity {planFacts.imputedDisparityElection ? 'on' : 'off'},
              rate banding {planFacts.rateBandingElection ? 'on' : 'off'}. The solver never changes a document election to obtain a pass.
            </p>
          </div>
        </details>
      </section>

      {/* ---- Step 3: Scenarios ---- */}
      <section className="space-y-2" data-testid="newcomp-step-scenarios">
        <StepHeading number={3} title="Scenarios" hint="Deterministic designs for this goal. Pick one to inspect its tests and worksheet." />
        {readiness.status === 'blocked' && (
          <p className="text-xs text-muted-foreground">Resolve the readiness blockers above to generate scenarios.</p>
        )}
        {readiness.status !== 'blocked' && solved?.status === 'blocked' && (
          <p className="text-xs text-muted-foreground">
            {solved.blockedStatus?.reasons?.map((reason) => reason.message).join(' ') || 'Scenarios cannot be generated yet.'}
          </p>
        )}
        {solved?.status === 'infeasible' && (
          <div className="rounded-md bg-red-500/10 p-2 text-xs text-red-700 dark:text-red-300" data-testid="newcomp-infeasible">
            <p className="font-medium">No scenario cleared the modeled constraints (binding constraint: {solved.diagnostics?.bindingConstraint}).</p>
            <p>{solved.diagnostics?.message}</p>
          </div>
        )}
        {solved?.status === 'solved' && (
          <div className="grid gap-2">
            {scenarios.map((scenario) => {
              const selected = selectedScenario?.id === scenario.id
              const proposalMetrics = proposalMetricsById[scenario.id]
              return (
                <button
                  key={scenario.id}
                  type="button"
                  onClick={() => { setSelectedScenarioId(scenario.id); setAcknowledged(false) }}
                  aria-pressed={selected}
                  data-testid={`newcomp-scenario-${scenario.id}`}
                  className={cn(
                    'rounded-md border p-2.5 text-left text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    selected ? 'border-primary bg-primary/[0.06]' : 'border-border/70 bg-background/60',
                  )}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-semibold">{scenario.label}</span>
                    <StatusBadge status={scenario.status.status}>{STATUS_LABELS[scenario.status.status]}</StatusBadge>
                  </div>
                  {scenario.totals.cashBalanceCents !== undefined ? (
                    <div className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-0.5 tabular-nums sm:grid-cols-5">
                      <span>Owner CB: <span className="font-medium">{dollars(scenario.totals.targetCashBalanceCents)}</span></span>
                      <span>Owner PS: <span className="font-medium">{dollars(scenario.totals.targetProfitSharingCents)}</span></span>
                      <span>Staff CB / PS: <span className="font-medium">{scenario.staffCbPercent.toFixed(2)}% / {scenario.staffPsPercent.toFixed(2)}%</span></span>
                      <span>Modeled design total: <span className="font-medium">{dollars(scenario.totals.employerCostCents)}</span></span>
                      <span>Owner share: <span className="font-medium">{scenario.totals.targetSharePercent.toFixed(1)}%</span></span>
                    </div>
                  ) : (
                    <div className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-0.5 tabular-nums sm:grid-cols-5">
                      <span>Owners: <span className="font-medium">{dollars(scenario.totals.targetProfitSharingCents)}</span></span>
                      <span>Everyone else: <span className="font-medium">{dollars(scenario.totals.nonTargetProfitSharingCents)}</span></span>
                      <span>Total cost: <span className="font-medium">{dollars(scenario.totals.employerCostCents)}</span></span>
                      <span>Owner share: <span className="font-medium">{scenario.totals.targetSharePercent.toFixed(1)}%</span></span>
                      <span>Min staff: <span className="font-medium">{scenario.totals.minimumStaffPercent.toFixed(2)}%</span></span>
                    </div>
                  )}
                  <p className="mt-1 text-muted-foreground">{scenario.explanation}</p>
                  {proposalMetrics && (
                    <p className="mt-1 border-t border-border/50 pt-1 text-muted-foreground">
                      Estimated tax savings {proposalDollars(proposalMetrics.estimatedTaxSavingsCents)}; after-tax cost{' '}
                      {proposalDollars(proposalMetrics.estimatedAfterTaxCostCents)}; cost per $1 of owner benefit{' '}
                      {proposalMetrics.employerCostPerOwnerBenefitDollar === null
                        ? 'N/A' : `$${proposalMetrics.employerCostPerOwnerBenefitDollar.toFixed(2)}`}.
                    </p>
                  )}
                </button>
              )
            })}
          </div>
        )}
      </section>

      {/* ---- Step 4: Tests and worksheet ---- */}
      {selectedScenario && (
        <section className="space-y-2" data-testid="newcomp-step-tests">
          <StepHeading number={4} title="Tests and worksheet" hint="Every screen, with the full status vocabulary. Modeled results, not determinations." />
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(selectedScenario.tests).map(([key, entry]) => (
              <StatusBadge key={key} status={entry?.status}>{TEST_BADGE_LABELS[key] || key}</StatusBadge>
            ))}
          </div>

          {proposalModel && (
            <div className="grid gap-2 rounded-md border border-border/70 bg-background/60 p-2.5 text-xs sm:grid-cols-4">
              <span>Owner benefit <strong className="block text-sm">{dollars(proposalModel.selectedMetrics.ownerBenefitCents)}</strong></span>
              <span>Modeled staff allocation <strong className="block text-sm">{dollars(proposalModel.selectedMetrics.staffCostCents)}</strong></span>
              <span>Estimated tax savings <strong className="block text-sm">{proposalDollars(proposalModel.selectedMetrics.estimatedTaxSavingsCents)}</strong></span>
              <span>After-tax cost <strong className="block text-sm">{proposalDollars(proposalModel.selectedMetrics.estimatedAfterTaxCostCents)}</strong></span>
              <div className="sm:col-span-4 border-t border-border/60 pt-2 text-muted-foreground">
                Safe-harbor cost comparison:{' '}
                {proposalModel.safeHarborAlternatives.map((entry) => `${entry.label} ${dollars(entry.employerCostCents)}`).join('; ')}.
              </div>
              {proposalModel.minimumParticipationHelper && (
                <div className="sm:col-span-4 text-muted-foreground">
                  401(a)(26) helper: {proposalModel.minimumParticipationHelper.benefitingCount} benefiting;
                  {' '}{proposalModel.minimumParticipationHelper.requiredBenefiting} required;
                  {' '}{proposalModel.minimumParticipationHelper.additionalBenefitingNeeded} additional participant(s) needed.
                </div>
              )}
            </div>
          )}

          <details className="rounded-md border border-dashed p-2 text-xs text-muted-foreground">
            <summary className="cursor-pointer font-medium text-foreground">Employee worksheet and professional detail</summary>
            <div className="mt-2 max-h-72 overflow-auto rounded-md border">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-card text-muted-foreground">
                  <tr className="border-b">
                    <th className="p-2 text-left font-medium">Employee</th>
                    <th className="p-2 text-left font-medium">Class</th>
                    <th className="p-2 text-right font-medium">Comp</th>
                    {cbEnabled && <th className="p-2 text-right font-medium">CB credit</th>}
                    <th className="p-2 text-right font-medium">Profit sharing</th>
                    <th className="p-2 text-right font-medium">EBAR</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedScenario.perPerson.map((row) => {
                    const testRow = selectedScenario.detail?.crossTesting?.rows?.find((entry) => entry.id === row.id)
                      || selectedScenario.detail?.combined?.rows?.find((entry) => entry.id === row.id)
                    const ebar = testRow?.ebar ?? testRow?.aggregateEbar
                    return (
                      <tr key={row.id} className="border-b last:border-0">
                        <td className="p-2">{nameById[row.id] || row.id}</td>
                        <td className="p-2 text-muted-foreground">{selectedScenario.groupAssignments[row.id] || STAFF_GROUP}</td>
                        <td className="p-2 text-right tabular-nums">{dollars(row.compensationCents)}</td>
                        {cbEnabled && (
                          <td className="p-2 text-right tabular-nums">{dollars(row.cbCreditCents || 0)}</td>
                        )}
                        <td className="p-2 text-right tabular-nums">
                          {dollars(row.profitSharingCents)}
                          {row.profitSharingReducedCents > 0 && (
                            <span className="block text-[10px] text-amber-700 dark:text-amber-300">415(c) reduced {dollars(row.profitSharingReducedCents)}</span>
                          )}
                        </td>
                        <td className="p-2 text-right tabular-nums">{ebar === undefined || ebar === null ? 'N/A' : `${ebar.toFixed(2)}%`}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            {selectedScenario.detail?.combined && (
              <div className="mt-2 space-y-1">
                <p>
                  Aggregate gateway ({selectedScenario.detail.combined.gatewayMethod}): required{' '}
                  {selectedScenario.detail.combined.requiredGatewayPercent?.toFixed(2)}% of §415(c) compensation
                  (highest HCE aggregate rate {selectedScenario.detail.combined.highestHceAggregateRate?.toFixed(2)}%,
                  sliding {selectedScenario.detail.combined.gatewayRequirement?.slidingPercent?.toFixed(2)}%, deemed cap 7.50%).
                  {selectedScenario.detail.combined.nhceDbAveragingElection
                    ? ` Averaged NHCE DB rate ${selectedScenario.detail.combined.averageNhceDbRatePercent?.toFixed(2)}%.`
                    : ' NHCE DB rates used individually (averaging not elected).'}
                </p>
                <p>
                  NHCE gateway sources: safe-harbor NEC {dollars(gatewayNhceSourceTotals.safeHarborNecCents)} counted;
                  {' '}other nonelective/QNEC/allocated forfeitures {dollars(gatewayNhceSourceTotals.otherNonelectiveCents)} counted;
                  {' '}additional profit sharing {dollars(gatewayNhceSourceTotals.profitSharingCents)} counted.
                  {' '}Match {dollars(gatewayNhceSourceTotals.matchCents)} and employee deferrals {dollars(gatewayNhceSourceTotals.deferralCents)} are excluded.
                </p>
                <p>
                  §401(a)(26): {selectedScenario.detail.combined.minimumParticipation.benefitingCount} of{' '}
                  {selectedScenario.detail.combined.minimumParticipation.requiredBenefiting} required participants at a
                  meaningful benefit. ABPT {selectedScenario.detail.combined.abptRatio?.toFixed(2)}%.
                  Outcome: {selectedScenario.detail.combined.aggregationOutcome}.
                </p>
                {(selectedScenario.limitations || []).map((limitation) => (
                  <p key={`${limitation.code}:${limitation.message}`}>Review: {limitation.message}</p>
                ))}
                {(selectedScenario.assumptions || []).map((assumption) => (
                  <p key={`${assumption.code}:${assumption.label}`}>Assumption: {assumption.label} ({String(assumption.value)}).</p>
                ))}
              </div>
            )}
            {selectedScenario.detail?.crossTesting && (
              <div className="mt-2 space-y-1">
                <p>
                  Gateway ({selectedScenario.detail.crossTesting.gatewayMinimumMethod}): operative rate {selectedScenario.detail.crossTesting.gatewayRate?.toFixed(2)}% on §415(c) compensation.
                  Plan ratio {selectedScenario.detail.crossTesting.planRatioPercent?.toFixed(1)}%, midpoint {selectedScenario.detail.crossTesting.midpointPercent?.toFixed(1)}%,
                  rate-group threshold {selectedScenario.detail.crossTesting.classificationThresholdPercent?.toFixed(1)}% ({selectedScenario.detail.crossTesting.classificationThresholdBasis}).
                  ABPT {selectedScenario.detail.crossTesting.abptRatio?.toFixed(2)}%.
                </p>
                <p>
                  Deduction screen: {dollars(selectedScenario.detail.deduction?.employerContributionCents)} of {dollars(selectedScenario.detail.deduction?.limitCents)} (25% of {dollars(selectedScenario.detail.deduction?.includedCompensationCents)} eligible compensation).
                </p>
                {(selectedScenario.limitations || []).map((limitation) => (
                  <p key={`${limitation.code}:${limitation.message}`}>Review: {limitation.message}</p>
                ))}
                {(selectedScenario.assumptions || []).map((assumption) => (
                  <p key={`${assumption.code}:${assumption.label}`}>Assumption: {assumption.label} ({String(assumption.value)}).</p>
                ))}
              </div>
            )}
            <p className="mt-2 text-xs text-muted-foreground">
              Client-facing proposal pages are part of Reports → PDF, where branding, assumptions, section order, and included pages can be customized.
            </p>
            <div className="mt-2 flex gap-2">
              <Button type="button" variant="outline" size="sm" className="h-7 gap-1.5 text-xs" onClick={() => handleDownload('csv')}>
                <Download className="h-3 w-3" /> TPA worksheets (CSV)
              </Button>
              <Button type="button" variant="outline" size="sm" className="h-7 gap-1.5 text-xs" onClick={() => handleDownload('json')}>
                <Download className="h-3 w-3" /> Calculation package (JSON)
              </Button>
            </div>
          </details>
        </section>
      )}

      {/* ---- Step 5: Apply ---- */}
      {selectedScenario && (
        <section className="space-y-2" data-testid="newcomp-step-apply">
          <StepHeading number={5} title="Apply" hint="Writes the grouped design, this plan's local assignments, and the proposal record together." />
          {selectedScenario.status.status === 'fails' || selectedScenario.status.status === 'unavailable' ? (
            <p className="rounded-md bg-red-500/10 p-2 text-xs text-red-700 dark:text-red-300">
              A design that is {selectedScenario.status.status === 'fails' ? 'flagged by a modeled screen' : 'unavailable for modeling'} cannot be applied.
            </p>
          ) : needsAcknowledgment ? (
            <label className="flex items-start gap-2 rounded-md border border-amber-400/60 bg-amber-500/5 p-2.5 text-xs">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(event) => setAcknowledged(event.target.checked)}
                className="mt-0.5"
                data-testid="newcomp-acknowledge"
              />
              <span>
                I understand this design is <span className="font-semibold">requires-review</span>: the open items above
                need a TPA or professional determination before the plan relies on it. The acknowledgment is stored with the proposal.
                <input
                  type="text"
                  value={ackNote}
                  onChange={(event) => setAckNote(event.target.value)}
                  placeholder="Optional note (who will confirm, by when)"
                  className="mt-1.5 w-full rounded border border-input bg-background px-2 py-1 text-xs"
                  aria-label="Acknowledgment note"
                />
              </span>
            </label>
          ) : (
            <p className="text-xs text-muted-foreground">This design has no modeled screen flags; that is not a formal compliance determination.</p>
          )}
          <p className="text-[11px] text-muted-foreground">
            Applying sets a grouped profit-sharing design and this plan&rsquo;s LOCAL participant assignments
            ({TARGET_GROUP} / {HCE_GROUP} / {STAFF_GROUP}) together.
            {selectedScenario.suggestedCashBalance
              ? ' The cash-balance credits for the same groups are written in the same transaction.'
              : ' Employee rows, match, and cash-balance groups are unchanged.'}
          </p>
        </section>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        <Button type="button" size="sm" onClick={handleApply} disabled={!applyReady} data-testid="newcomp-apply">
          Apply design
        </Button>
      </div>
    </div>
  )
}
